// A signpost, described the way age-of-mcp authors it: a `post` helper and an
// `arm` helper for the pointing direction arms, plus the shared `foundationSlab`
// base, walked through its build stages. Ported 1:1 from age-of-mcp's signpost.ts.
import * as THREE from "three";
import { defineModel, foundationSlab, type Part } from "../../lib";

const POST = "#7a5a32";
const ARM = "#a8853f";

// A wooden post `h` tiles tall.
function post(h: number): Part {
  return {
    geometry: new THREE.BoxGeometry(0.04, h, 0.04),
    color: POST,
    position: [0, h / 2, 0],
  };
}

// A direction arm pointing along `dir` (+1 = east, -1 = west) at height `y`.
function arm(y: number, dir: 1 | -1): Part {
  return {
    geometry: new THREE.BoxGeometry(0.2, 0.055, 0.035),
    color: ARM,
    position: [dir * 0.1, y, 0],
  };
}

// The build stages of the signpost: foundation, sprouting post, taller post,
// and the finished post with two pointing direction arms.
function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.18, 0.05)];
  if (s === 1) return [post(0.22)];
  if (s === 2) return [post(0.42)];
  return [post(0.5), arm(0.42, 1), arm(0.32, -1)];
}

export const signpost = defineModel({
  id: "signpost",
  tiles: { w: 1, h: 1 },
  batchId: "standard",
  name: "Signpost",
  tags: ["building","signpost","low-poly","age-of-mcp","classic"],
  summary: "Low-poly signpost.",
  description: "A post with arms, each carrying a short label pointing a compass direction.",
  author: "age-of-mcp",
  footprint: 0.13,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
